import React, { useContext, useEffect, useRef, useState } from 'react';
import { useAppDispatch } from 'hooks';
import { skipToken } from '@reduxjs/toolkit/query';
import { Progress } from 'reactstrap';

import { VALIDATION_POLL_INTERVAL_MS } from '~constants/common';

import { getErrorMessage } from '~libs/axiosWrapper';
import { pluralize } from '~libs/stringarly';
import { useToast } from '~libs/toast';

import { Button, ErrorResponse, ModalCard } from '~components';
import Icon, { faFileCsv } from '~components/icon';
import { TableView } from '~components/table-view';

import buildColumns from '~support-at-home/forms/budgets/bulkCopyPreviewList';

import {
  budgetsApi,
  BudgetPreviewRow,
  useBulkCopyMutation,
  useGetBulkCopyRunQuery,
  useLazyGetActiveBulkCopyRunQuery,
} from '~support-at-home/actions/budgets';

import { BulkCopyCsvRow, exportBulkCopyReviewCsv } from './bulkCopyCsv';
import { BulkCopyLockContext } from './BulkCopyLockContext';
import FailureList from './FailureList';

interface ComponentProps {
  value: {
    budget_ids: string[];
    start_date: string;
    end_date: string;
    price_plan_revision_id?: string;
    apply_category_moves?: boolean;
  };
  onChange?: (name: string, value: unknown) => void;
}

const Review: React.FC<ComponentProps> = ({ value, onChange }) => {
  const { start_date, end_date, budget_ids, price_plan_revision_id, apply_category_moves } = value;
  const dispatch = useAppDispatch();
  const { showToast } = useToast();
  // Lock the enclosing modal shut while a copy is running (see BulkCopyLockContext).
  const setModalLocked = useContext(BulkCopyLockContext);

  const [bulkCopy] = useBulkCopyMutation();
  const [getActiveRun] = useLazyGetActiveBulkCopyRunQuery();

  const [runId, setRunId] = useState<string | null>(null);
  const [startError, setStartError] = useState<string | null>(null);
  // True while a kick-off/retry mutation is in flight. Needed because `run` still holds its PREVIOUS terminal
  // status until the new run id lands, so without this the terminal view (and its Retry button) stays live
  // and interactive during the request — a second click would submit the same ids again and the API's
  // duplicate-active-run 409 would replace the results the coordinator was reading with an error.
  const [isStartingCopy, setIsStartingCopy] = useState(false);

  const startedRef = useRef(false);
  const completedToastRef = useRef(false);
  const invalidatedRef = useRef(false);
  const copyInProgressRef = useRef<boolean | null>(null);

  const {
    data: run,
    error: runError,
    refetch: refetchRun,
  } = useGetBulkCopyRunQuery(runId ?? skipToken, {
    refetchOnMountOrArgChange: true,
  });

  // Copy is in progress from the moment the step mounts (kick-off/adopt) until the run reaches a terminal
  // state or either request errors. Drives both the modal lock (context) and the Formik copy_in_progress flag
  // (which hides the wizard footer's Close button on this step — see bulkCopy.ts). A failed run-status query
  // must break the lock too, otherwise an expired/404 run would strand the user on "Please wait" with no exit.
  // isStartingCopy covers the gap where a retry mutation is in flight but `run` is still the old terminal one.
  const copying = isStartingCopy || (!startError && !runError && (!run || run.status === 'running'));

  const startCopy = async (idsToCopy: string[]) => {
    setIsStartingCopy(true);
    try {
      const { runId: newRunId } = await bulkCopy({
        budget_ids: idsToCopy,
        start_date,
        end_date,
        target_revision_id: price_plan_revision_id ?? null,
        // Only ever sent as false - the preview step publishes the value, and a wizard that never
        // reached it (or an older cached value) means nobody asked for the services to be dropped.
        apply_category_moves: apply_category_moves !== false,
      }).unwrap();
      setStartError(null);
      setRunId(newRunId);
    } catch (err) {
      setStartError(getErrorMessage(err));
    } finally {
      setIsStartingCopy(false);
    }
  };

  // Determine the run once, on mount: adopt an already-active run (re-entering this step, or a
  // transient poll drop) rather than kicking off a duplicate copy (the API 409s on a duplicate
  // active run anyway). If there's no active run, start a new one.
  useEffect(() => {
    if (startedRef.current) return;
    startedRef.current = true;

    (async () => {
      try {
        const activeRun = await getActiveRun().unwrap();
        if (activeRun) {
          setRunId(activeRun.id);
          return;
        }
        await startCopy(budget_ids);
      } catch (err) {
        setStartError(getErrorMessage(err));
      }
    })();
    // Guarded by startedRef so this only ever runs once per wizard step entry, even though
    // WizardForm remounts each step.
  }, []);

  // Lock the modal while copying; release it once the run reaches a terminal state (or the kick-off errors).
  useEffect(() => {
    setModalLocked(copying);
  }, [copying, setModalLocked]);

  // Publish copy_in_progress to Formik (ref-guarded to avoid render loops — onChange isn't referentially
  // stable) so the wizard footer's Close button is hidden on this step while copying.
  useEffect(() => {
    if (copyInProgressRef.current === copying) return;
    copyInProgressRef.current = copying;
    onChange?.('copy_in_progress', copying);
  }, [copying, onChange]);

  // Poll for completion while running (mirrors Claim.tsx's is_validating poll).
  useEffect(() => {
    if (!run || run.status !== 'running') return undefined;

    const interval = setInterval(() => {
      refetchRun();
    }, VALIDATION_POLL_INTERVAL_MS);

    return () => clearInterval(interval);
  }, [run?.status, refetchRun]);

  // Every budget the run could not copy — both the retryable per-budget failures and the terminal
  // unresolvable ids. This, not run.failed_count, is what the UI counts: failed_count covers only the
  // retryable bucket, so using it let a run whose budgets were all unresolvable report a clean success while
  // the header showed a shortfall it never explained (TAV-2649a review feedback).
  const failures = run?.failures ?? [];
  const notCopiedCount = failures.length;

  // Invalidate the budgets list on ANY terminal status. A 'failed' run can still have created budgets before
  // it died (see the results table below, which reports exactly that), so gating this on 'completed' left the
  // list stale after the wizard closed. Separate from the toast effect: the toast is a success/partial
  // message that only makes sense for a completed run, whereas the cache is stale either way.
  useEffect(() => {
    if (run?.status !== 'completed' && run?.status !== 'failed') return;
    if (invalidatedRef.current) return;
    invalidatedRef.current = true;
    dispatch(budgetsApi.util.invalidateTags([{ type: 'budgets', id: 'LIST' }]));
  }, [run?.status, dispatch]);

  // On completion: toast once.
  useEffect(() => {
    if (run?.status !== 'completed' || completedToastRef.current) return;
    completedToastRef.current = true;
    // A completed run can still have budgets it couldn't copy, so don't call that a clean success —
    // say so in the toast and point at the table below (TAV-2649a).
    const { pluralNoun } = pluralize(run.copied_count, 'budget');
    if (notCopiedCount > 0) {
      showToast(
        'info',
        `${run.copied_count} ${pluralNoun} copied - ${notCopiedCount} could not be copied, see the list below`,
      );
    } else {
      showToast('success', `${run.copied_count} ${pluralNoun} copied successfully`);
    }
  }, [run?.status, run?.copied_count, notCopiedCount, showToast]);

  // Safety: if this step ever unmounts, release the modal lock so it can't get stuck closed-off.
  useEffect(() => () => setModalLocked(false), [setModalLocked]);

  const handleRetry = () => {
    const remaining = run?.remaining_budget_ids ?? [];
    if (remaining.length === 0 || isStartingCopy) return;
    // Allow the retry run to toast on its own completion, and to invalidate the budgets list again — both
    // refs latched on the previous run's terminal state and the retry creates budgets of its own.
    completedToastRef.current = false;
    invalidatedRef.current = false;
    startCopy(remaining);
  };

  const toCsvRows = (budgets: BudgetPreviewRow[]): BulkCopyCsvRow[] =>
    budgets.map((budget) => ({
      client_name: budget.client_name,
      budget_id: budget.budget_id,
      budget_reference: budget.ref,
      start_date,
      end_date,
      services_removed: budget.removed_count,
      // Named here as well as on the preview, so the record a provider keeps after the run tells them
      // which services changed category rather than only that some number of them did.
      services_moved: (budget.moved_services ?? [])
        .map((moved) => `${moved.service_type_name ?? 'Service'} \u2192 ${moved.to_category_name ?? 'new category'}`)
        .join('; '),
      services_price_missing: budget.price_missing_count,
      needs_review: budget.needs_review,
    }));

  if (startError || runError) {
    return <ErrorResponse message={startError ?? getErrorMessage(runError)} />;
  }

  if (!run || run.status === 'running') {
    return (
      <div>
        <div className="text-center py-5">
          <p className="mb-2">Please wait - copying budgets…</p>
          <Progress value={100} style={{ height: '25px' }} color="success" animated striped />
          <p className="mt-3 mb-0">
            {run ? `${run.copied_count} of ${run.total_count} budgets copied so far` : 'Starting…'}
            {notCopiedCount > 0 && ` - ${notCopiedCount} could not be copied`}
          </p>
          <p className="text-danger fw-semibold mt-3 mb-0">
            Please do not close or refresh the page while copying.
          </p>
        </div>
        {/* The failure list is sent for every run status, so it builds up live while the copy is still
            going (TAV-2649a). */}
        <FailureList failures={failures} headingSuffix="Not Copied" idPrefix="bulk-copy-review" showRetryable />
      </div>
    );
  }

  // Terminal run (completed or failed). Both cases render the SAME two tables — what copied and what
  // didn't — because a partially-successful run is now the normal outcome, not an exception: bulkCopyBudgets
  // skips the budgets it can't copy rather than aborting the batch. A 'failed' status on top of that means
  // something batch-fatal ALSO happened (timeout, dead worker, bad target revision), so its error message is
  // shown above the tables instead of replacing them — previously it replaced them and the coordinator was
  // left with a bare "625 of 923" and no idea which budgets those were.
  //
  // copied_count / needs_review_count are the authoritative server totals; `run.budgets` could in principle
  // be a subset (a copied budget deleted before the poll returns), so it must NOT drive the header or the
  // flagged count — otherwise the header would contradict the success toast.
  const budgets = run.budgets ?? [];
  const rows = budgets.map((budget) => ({ ...budget, id: budget.budget_id }));
  // Same rule as the preview: the column is here because this run HAD services changing category, so
  // the record of what was created says how many moved on each budget.
  const hasMovedServices = budgets.some((budget) => (budget.moved_count ?? 0) > 0);
  const isPartialList = budgets.length < run.copied_count;
  const remainingCount = run.remaining_budget_ids?.length ?? 0;
  // Named because the retry button needs it twice — once in the label, once in the aria-label that keeps the
  // accessible name alive while the spinner replaces the children.
  const retryNoun = pluralize(remainingCount, 'budget').pluralNoun;

  return (
    <div>
      {run.status === 'failed' && <ErrorResponse message={run.error_message ?? 'The copy failed.'} />}
      <ModalCard header={`${run.copied_count} of ${run.total_count} New Budgets Created`} className="w-100">
        {isPartialList && (
          <p className="text-muted mb-2">
            Showing {budgets.length} of {run.copied_count} copied budgets.
          </p>
        )}
        {/* Height-capped + scrollable: see .bulk-copy-scroll-table in _claims.scss */}
        <div className="bulk-copy-scroll-table">
          <TableView
            id="bulk-copy-estimate-table"
            data={rows}
            columns={buildColumns(hasMovedServices)}
            readonly
            striped
          />
        </div>
        <div className="d-flex align-items-center justify-content-between mt-3">
          <p className="mb-0">
            {run.needs_review_count} {pluralize(run.needs_review_count, 'budget').pluralNoun} flagged for review
          </p>
          {budgets.length > 0 && (
            <Button
              color="info"
              onClick={() => exportBulkCopyReviewCsv(toCsvRows(budgets))}
              dataCy="bulk-copy-review-export-button"
            >
              <Icon icon={faFileCsv} className="me-2" />
              Download copied budgets
            </Button>
          )}
        </div>
      </ModalCard>
      <FailureList failures={failures} headingSuffix="Not Copied" idPrefix="bulk-copy-review" showRetryable />
      {remainingCount > 0 && (
        <div className="d-flex justify-content-end mt-3">
          {/* `loading` shows the spinner while the retry mutation is in flight — this view stays rendered
              throughout, since `run` keeps its previous terminal status until the new run id lands, so
              without it the only feedback is a dead-looking button. `disabled` is kept explicit even though
              `loading` already disables: it's the double-submit guard, and shouldn't depend on a Button
              internal. aria-label preserves the accessible name, which `loading` would otherwise strip by
              replacing the children with the spinner. */}
          <Button
            color="success"
            onClick={handleRetry}
            loading={isStartingCopy}
            disabled={isStartingCopy}
            aria-label={`Retry remaining ${remainingCount} ${retryNoun}`}
            dataCy="bulk-copy-review-retry-button"
          >
            Retry remaining {remainingCount} {retryNoun}
          </Button>
        </div>
      )}
    </div>
  );
};

export default Review;
